import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Tabs } from '@spark';

import SettingsSectionCard from '@/features/settings/components/SettingsSectionCard.jsx';
import VendorKeysForm, { VENDOR_PROVIDERS } from '@/features/settings/components/providers/VendorKeysForm.jsx';
import BedrockCredentialsForm, {
    BEDROCK_REQUIRED_FIELDS,
} from '@/features/settings/components/providers/BedrockCredentialsForm.jsx';
import VertexCredentialsForm from '@/features/settings/components/providers/VertexCredentialsForm.jsx';
import AzureCredentialsForm from '@/features/settings/components/providers/AzureCredentialsForm.jsx';
import {
    hasAzureCredentials,
    hasEffectiveVertexProvider,
    hasVertexCredentials,
} from '@/features/settings/hooks/useOrganizationProviderSettings';
import './settings.css';

const CLOUD_PROVIDERS = [
    { value: 'bedrock', label: 'Bedrock' },
    { value: 'vertex', label: 'VertexAI' },
    { value: 'azure', label: 'AzureAI' },
];

// Tab bar display order: main vendors, then cloud services, with the niche
// vendors pinned to the end — TypeSafe AI second-to-last, DeepSeek last.
const TAB_END_PROVIDER_IDS = ['jev', 'deepseek'];
const TAB_PROVIDERS = [
    ...VENDOR_PROVIDERS.filter((p) => !TAB_END_PROVIDER_IDS.includes(p.value)),
    ...CLOUD_PROVIDERS,
    ...TAB_END_PROVIDER_IDS.map((value) =>
        VENDOR_PROVIDERS.find((p) => p.value === value)
    ).filter(Boolean),
];

const hasVendorKeys = (settings, provider) =>
    Array.isArray(settings?.[`${provider}_keys`]) &&
    settings[`${provider}_keys`].length > 0;

const hasBedrockCredentials = (settings) =>
    BEDROCK_REQUIRED_FIELDS.every((field) => Boolean(settings?.[field]));

// Active-credential count shown on each provider tab (Figma 6001-1017,
// e.g. "Open AI (1)"). Vendors count saved keys; cloud providers hold a
// single credential set, so they report 1 or 0. Vertex counts the
// *effective* provider rather than only a saved one: on a GCP-hosted
// platform it is live by default through Workload Identity, and a (0)
// next to a panel that says "enabled by default" reads as a contradiction.
const providerKeyCount = (settings, provider) => {
    if (provider === 'bedrock') return hasBedrockCredentials(settings) ? 1 : 0;
    if (provider === 'vertex') return hasEffectiveVertexProvider(settings) ? 1 : 0;
    if (provider === 'azure') return hasAzureCredentials(settings) ? 1 : 0;
    return Array.isArray(settings?.[`${provider}_keys`])
        ? settings[`${provider}_keys`].length
        : 0;
};

/**
 * Number of providers with at least one active credential — the Settings
 * sidebar's "Model Providers (N)". Counts providers, not keys: three OpenAI
 * keys are one configured provider. Built on the same per-provider rule as
 * the tab counts above, so the sidebar total is always the number of tabs
 * showing a non-zero count.
 */
export const countConfiguredProviders = (settings) =>
    TAB_PROVIDERS.filter((p) => providerKeyCount(settings, p.value) > 0).length;

/**
 * Provider tabs + panel (Figma frames 6001-1017 / 6006-368).
 * Onboarding modal (``lockDefaultTab``) always starts on Open AI.
 * Settings page may jump to the first provider that already has credentials
 * and shows the active key count on each tab (``showKeyCounts``).
 */
function ModelProvidersPanel({
    settings,
    onVendorKeysUpdate,
    onRecommendedProviderUpdate,
    onBedrockUpdate,
    onBedrockClear,
    onVertexUpdate,
    onVertexClear,
    onVertexTestConnection,
    onAzureUpdate,
    onAzureClear,
    onVendorDraftStateChange,
    vendorDraftSubmitRef,
    lockDefaultTab = false,
    showKeyCounts = false,
    initialProvider = null,
    focusField = null,
    platformCloudProvider = '',
}) {
    // ``initialProvider`` comes from the ``?provider=`` deep-link param
    // (e.g. the evaluation banner's "Add Azure Global-Batch deployment" fix)
    // and wins over the saved-credentials default below.
    const isKnownProvider = [...VENDOR_PROVIDERS, ...CLOUD_PROVIDERS].some(
        (p) => p.value === initialProvider
    );
    const [activeProvider, setActiveProvider] = useState(
        isKnownProvider ? initialProvider : 'openai'
    );

    const bedrockCredentials = useMemo(() => ({
        aws_access_key_id: settings.aws_access_key_id || '',
        aws_secret_access_key: settings.aws_secret_access_key || '',
        aws_region: settings.aws_region || '',
        aws_s3_bucket: settings.aws_s3_bucket || '',
        aws_role_arn: settings.aws_role_arn || '',
    }), [settings]);

    // Default the selection to whichever provider already has saved credentials
    // the first time settings arrive (Settings page only). Onboarding locks
    // Open AI; a provider deep link locks its own tab.
    const defaultsInitializedRef = useRef(lockDefaultTab || isKnownProvider);
    useEffect(() => {
        if (defaultsInitializedRef.current) return;

        const vendorWithKeys = VENDOR_PROVIDERS.find((p) =>
            hasVendorKeys(settings, p.value)
        );
        if (vendorWithKeys) {
            setActiveProvider(vendorWithKeys.value);
            defaultsInitializedRef.current = true;
            return;
        }
        if (hasBedrockCredentials(settings)) {
            setActiveProvider('bedrock');
            defaultsInitializedRef.current = true;
            return;
        }
        if (hasVertexCredentials(settings)) {
            setActiveProvider('vertex');
            defaultsInitializedRef.current = true;
            return;
        }
        if (hasAzureCredentials(settings)) {
            setActiveProvider('azure');
            defaultsInitializedRef.current = true;
        }
    }, [settings]);

    const tabItems = useMemo(
        () =>
            TAB_PROVIDERS.map((p) => ({
                id: p.value,
                label: showKeyCounts
                    ? `${p.label} (${providerKeyCount(settings, p.value)})`
                    : p.label,
            })),
        [settings, showKeyCounts]
    );

    const recommendedProps = {
        recommendedProvider: settings.recommended_provider,
        onRecommendedProviderUpdate,
    };

    const renderPanel = () => {
        if (VENDOR_PROVIDERS.some((p) => p.value === activeProvider)) {
            return (
                <VendorKeysForm
                    provider={activeProvider}
                    apiKeys={settings[`${activeProvider}_keys`] || []}
                    onKeysUpdate={(keys, action) =>
                        onVendorKeysUpdate(activeProvider, keys, action)
                    }
                    onDraftStateChange={onVendorDraftStateChange}
                    submitDraftRef={vendorDraftSubmitRef}
                    {...recommendedProps}
                />
            );
        }
        if (activeProvider === 'bedrock') {
            return (
                <BedrockCredentialsForm
                    credentials={bedrockCredentials}
                    onCredentialsUpdate={onBedrockUpdate}
                    onCredentialsClear={onBedrockClear}
                    {...recommendedProps}
                />
            );
        }
        if (activeProvider === 'vertex') {
            return (
                <VertexCredentialsForm
                    credentials={settings.vertex || {}}
                    onCredentialsUpdate={onVertexUpdate}
                    onCredentialsClear={onVertexClear}
                    onTestConnection={onVertexTestConnection}
                    platformCloudProvider={platformCloudProvider}
                    {...recommendedProps}
                />
            );
        }
        if (activeProvider === 'azure') {
            return (
                <AzureCredentialsForm
                    credentials={settings.azure || {}}
                    onCredentialsUpdate={onAzureUpdate}
                    onCredentialsClear={onAzureClear}
                    focusField={focusField}
                    {...recommendedProps}
                />
            );
        }
        return null;
    };

    return (
        <div className="settings-providers fs-unmask">
            <Tabs
                items={tabItems}
                activeId={activeProvider}
                onChange={setActiveProvider}
                size="large"
                position="top"
                scrollable
            />
            {renderPanel()}
        </div>
    );
}

/**
 * Model Providers section. When ``embedded`` is true (onboarding modal), skip
 * SettingsSectionCard so the parent Modal owns the title, and lock the default
 * tab to Open AI.
 */
const ModelProviders = ({
    embedded = false,
    ...panelProps
}) => {
    const panel = (
        <ModelProvidersPanel
            {...panelProps}
            lockDefaultTab={embedded}
            showKeyCounts={!embedded}
        />
    );

    if (embedded) {
        return panel;
    }

    return (
        <SettingsSectionCard iconSlug="model-providers" title="Model Providers">
            {panel}
        </SettingsSectionCard>
    );
};

export default ModelProviders;
